/**
 * Строка справочника с картинкой — и просмотр движения перед выбором.
 *
 * Зачем. Человек выбирает упражнение по названию и не всегда знает, что за
 * ним стоит: «Тяга горизонтального блока» или «Разведение в тренажёре» —
 * слова, которые в зале ни о чём не говорят, пока не увидишь снаряд.
 *
 * ПОСМОТРЕТЬ И ВЫБРАТЬ — РАЗНЫЕ КНОПКИ, и это главное решение файла. Тап по
 * названию выбирает движение, как и раньше, а в «Заменить» и на экране
 * тренировки выбор сразу меняет план на сервере (у процентной программы —
 * необратимо). Поэтому картинка — отдельная кнопка слева (`ExerciseThumb`), со
 * своей зоной касания и зазором до названия: «хотел посмотреть — случайно
 * выбрал» не должно случаться. Выбрать можно и из просмотра, кнопкой над
 * кадрами.
 */

import { useEffect, useRef, type ReactNode } from 'react'

import type { Exercise } from '../api/types'
import { ExerciseThumb, type ExerciseRef } from './ExerciseThumb'
import { TechniqueBody } from './TechniqueCard'

export function ExerciseRow({
  exercise,
  reason,
  pill,
  disabled,
  onPick,
  onPeek,
  extra,
}: {
  exercise: Exercise
  /** Причина второй строкой: справа пилюлей она сжимала бы название в столбик. */
  reason?: string
  pill?: string
  disabled: boolean
  onPick: () => void
  onPeek: () => void
  /** Дополнительная кнопка справа (убрать своё движение). */
  extra?: ReactNode
}) {
  return (
    <div className="se-row xp-row">
      <ExerciseThumb exercise={exercise} onPeek={onPeek} />
      <button type="button" className="se-pick xp-pick" disabled={disabled} onClick={onPick}>
        <span className="xp-text">
          <span>{exercise.name}</span>
          {reason && <span className="xp-reason">{reason}</span>}
        </span>
        {pill && <span className="pill">{pill}</span>}
      </button>
      {extra}
    </div>
  )
}

/**
 * Просмотр движения: кадры, пометка варианта, текст техники, источники.
 *
 * Открывается на месте списка, а не внутри него: высокий кадр в блоке высотой
 * 300 px утащил бы кнопку «Выбрать» за край. Кнопки стоят НАД кадрами — чтобы
 * решение не требовало прокрутки.
 */
export function ExercisePeek({
  exercise,
  pickLabel,
  disabled,
  onPick,
  onBack,
}: {
  exercise: ExerciseRef
  pickLabel: string
  disabled: boolean
  onPick: () => void
  onBack: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  // Просмотр встаёт на место списка, а страницу могли прокрутить далеко вниз:
  // кнопки «К списку» и выбора оказались бы над краем экрана — или под
  // статус-баром и полоской синхронизации, у которой тап означает повтор
  // отправки. Порог — тот же `scroll-margin-top`, что задан в CSS: число живёт
  // в одном месте.
  useEffect(() => {
    const node = ref.current
    if (!node) return
    const margin = parseFloat(getComputedStyle(node).scrollMarginTop) || 0
    if (node.getBoundingClientRect().top < margin) node.scrollIntoView({ block: 'start' })
  }, [exercise.id])

  return (
    <div className="xp-peek" ref={ref}>
      <div className="se-actions" style={{ marginTop: 0 }}>
        <button type="button" className="btn ghost" onClick={onBack}>
          ← К списку
        </button>
        <button type="button" className="btn primary" disabled={disabled} onClick={onPick}>
          {pickLabel}
        </button>
      </div>

      <div className="ex-name" style={{ marginTop: 12 }}>
        {exercise.name}
      </div>

      <TechniqueBody exerciseId={exercise.id} name={exercise.name} />
    </div>
  )
}
